import { create } from "zustand";
import type { ChatMessage } from "shared";

/** UI message model (broadcast ChatMessage plus a client id and parsed files). */
export interface UiMessage {
  key: string;
  userId: string;
  name: string;
  contents: string;
  imageUrl?: string;
  system: boolean;
  files: string[];
  mine: boolean;
}

export interface PdfState {
  pages: string[];
}

export type FileScreenEntry = { files: string[]; title?: string };

interface GameState {
  connected: boolean;
  messages: UiMessage[];
  caption: string | null;
  cueImage: string | null;
  puzzlePrimary: string | null;
  puzzleSecondary: string | null;
  puzzleVisible: boolean;
  files: FileScreenEntry[] | null;
  pdf: PdfState | null;
  clueText: string | null;

  setConnected: (v: boolean) => void;
  addMessage: (m: UiMessage) => void;
  setMessages: (m: UiMessage[]) => void;
  setCaption: (c: string | null) => void;
  setCueImage: (src: string | null) => void;
  setPuzzle: (primary: string | null) => void;
  setPuzzleSecondary: (secondary: string | null) => void;
  setPuzzleVisible: (v: boolean) => void;
  setFiles: (f: FileScreenEntry[] | null) => void;
  openPdf: (pages: string[]) => void;
  closePdf: () => void;
  setClue: (text: string | null) => void;
  /** Reset transient per-stage UI when the group progresses. */
  onProgressed: () => void;
}

export const useGame = create<GameState>((set) => ({
  connected: false,
  messages: [],
  caption: null,
  cueImage: null,
  puzzlePrimary: null,
  puzzleSecondary: null,
  puzzleVisible: false,
  files: null,
  pdf: null,
  clueText: null,

  setConnected: (connected) => set({ connected }),
  addMessage: (m) => set((s) => ({ messages: [...s.messages, m] })),
  setMessages: (messages) => set({ messages }),
  setCaption: (caption) => set({ caption }),
  setCueImage: (cueImage) => set({ cueImage }),
  setPuzzle: (puzzlePrimary) => set({ puzzlePrimary, puzzleVisible: puzzlePrimary !== null }),
  setPuzzleSecondary: (puzzleSecondary) => set({ puzzleSecondary }),
  setPuzzleVisible: (puzzleVisible) => set({ puzzleVisible }),
  setFiles: (files) => set({ files }),
  openPdf: (pages) => set({ pdf: { pages } }),
  closePdf: () => set({ pdf: null }),
  setClue: (clueText) => set({ clueText }),
  onProgressed: () => set({ caption: null, cueImage: null }),
}));

/** Helper to turn a broadcast ChatMessage into a UiMessage. */
export function toUiMessage(m: ChatMessage, mine: boolean, key: string): UiMessage {
  let files: string[] = [];
  if (m.files && m.files !== "undefined" && m.files !== "null") {
    try {
      const parsed: unknown = JSON.parse(m.files);
      if (Array.isArray(parsed)) files = parsed as string[];
    } catch {
      files = [];
    }
  }
  return {
    key,
    userId: m.user_id,
    name: m.name,
    contents: m.contents,
    imageUrl: m.image_url,
    system: m.system ?? false,
    files,
    mine,
  };
}
